import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BarChart3, TrendingUp, Target, Users, Award, Zap, AlertTriangle,
  CheckCircle2, AlertCircle, Database, ChevronRight, ArrowUp, ArrowDown,
  Minus, Info, RefreshCw, Download, Loader2, PieChart, Activity, Gauge,
  Filter, Building2, GraduationCap, LineChart as LineChartIcon
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Cell, Legend
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { NavigationChainHealth } from '../components/navigation-health/NavigationChainHealth';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import PinnedReportsSection from '../components/admin/PinnedReportsSection';
import ProactiveInsightsCard from '../components/admin/ProactiveInsightsCard';
import { getLightSectionTheme } from '../lib/sectionThemes';
import { buildConditions, conditionBand } from '../lib/leiConditions';
import { NavigationConditionsRibbon } from '../components/lei-conditions/NavigationConditionsRibbon';
import { ConditionPropagation } from '../components/lei-conditions/ConditionPropagation';
import { ConversionEfficiencyCard } from '../components/lei-conditions/ConversionEfficiencyCard';
import { NavigationConstraintsPanel } from '../components/lei-conditions/NavigationConstraintsPanel';
import { ConditionsGrid } from '../components/lei-conditions/ConditionsGrid';
import { NavigationSummaryCard } from '../components/navigation-signals/NavigationSummaryCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pillar color mapping
const PILLAR_COLORS = {
  P1: '#3B82F6', // Blue
  P2: '#10B981', // Green
  P3: '#8B5CF6', // Purple
  P4: '#F59E0B', // Amber
  P5: '#EF4444'  // Red
};

// Status colors
const STATUS_COLORS = {
  on_track: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  at_risk: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  off_track: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' }
};

// Overall LEI Score Gauge
const LEIScoreGauge = ({ score, status }) => {
  const getStatusColor = () => {
    if (score >= 75) return '#10B981';
    if (score >= 60) return '#3B82F6';
    if (score >= 45) return '#F59E0B';
    return '#EF4444';
  };
  
  const angle = (score / 100) * 180;
  
  return (
    <div className="relative w-64 h-32 mx-auto">
      <svg viewBox="0 0 200 100" className="w-full h-full">
        {/* Background arc */}
        <path
          d="M 10 100 A 90 90 0 0 1 190 100"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="12"
          strokeLinecap="round"
        />
        {/* Score arc */}
        <path
          d="M 10 100 A 90 90 0 0 1 190 100"
          fill="none"
          stroke={getStatusColor()}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={`${(score / 100) * 283} 283`}
        />
        {/* Score text */}
        <text x="100" y="85" textAnchor="middle" className="text-4xl font-bold" fill={getStatusColor()}>
          {score.toFixed(0)}
        </text>
        <text x="100" y="98" textAnchor="middle" className="text-xs" fill="#6B7280">
          Overall LEI Score
        </text>
      </svg>
    </div>
  );
};

// Pillar Card Component
const PillarCard = ({ pillar, onClick }) => {
  const statusCounts = pillar.measures?.reduce((acc, m) => {
    acc[m.status] = (acc[m.status] || 0) + 1;
    return acc;
  }, {}) || {};
  
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border p-4 cursor-pointer hover:shadow-md transition-shadow"
      data-testid={`pillar-card-${pillar.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: pillar.color }}
          />
          <span className="font-semibold text-gray-900">{pillar.short_name}</span>
        </div>
        <span className="text-xs bg-gray-100 px-2 py-1 rounded">
          {(pillar.weight * 100).toFixed(0)}% weight
        </span>
      </div>
      
      <div className="flex items-end justify-between mb-3">
        <div>
          <span className="text-3xl font-bold" style={{ color: pillar.color }}>
            {pillar.score?.toFixed(0) || 0}
          </span>
          <span className="text-gray-500 text-sm ml-1">/100</span>
        </div>
        <div className="text-right">
          <span className="text-xs text-gray-500">Weighted</span>
          <p className="font-semibold text-gray-700">{pillar.weighted_score?.toFixed(1) || 0}</p>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pillar.score || 0}%`, backgroundColor: pillar.color }}
        />
      </div>
      
      {/* Status indicators */}
      <div className="flex items-center gap-3 text-xs">
        <span className="flex items-center gap-1 text-emerald-600">
          <CheckCircle2 size={12} /> {statusCounts.on_track || 0}
        </span>
        <span className="flex items-center gap-1 text-yellow-600">
          <AlertCircle size={12} /> {statusCounts.at_risk || 0}
        </span>
        <span className="flex items-center gap-1 text-rose-600">
          <AlertTriangle size={12} /> {statusCounts.off_track || 0}
        </span>
        <ChevronRight className="ml-auto text-gray-400" size={16} />
      </div>
    </div>
  );
};

// Measure Row Component
const MeasureRow = ({ measure, onClick }) => {
  const statusStyle = STATUS_COLORS[measure.status] || STATUS_COLORS.off_track;
  
  return (
    <div
      onClick={onClick}
      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
      data-testid={`measure-row-${measure.id}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-2 h-8 rounded-full"
          style={{ backgroundColor: measure.pillar_color }}
        />
        <div>
          <span className="font-medium text-gray-900">{measure.name}</span>
          <p className="text-xs text-gray-500">{measure.pillar_short_name}</p>
        </div>
      </div>
      
      <div className="flex items-center gap-6">
        <div className="text-right">
          <span className="font-semibold text-gray-900">{measure.value?.toFixed(0) || 0}</span>
          <span className="text-gray-400 text-sm">/{measure.target}</span>
        </div>
        
        <div className="w-24">
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min(100, measure.progress || 0)}%`,
                backgroundColor: measure.pillar_color
              }}
            />
          </div>
        </div>
        
        <span className={`text-xs px-2 py-1 rounded ${statusStyle.bg} ${statusStyle.text}`}>
          {measure.status?.replace('_', ' ')}
        </span>
        
        <ChevronRight className="text-gray-400" size={16} />
      </div>
    </div>
  );
};

// Measure Detail Modal
const MeasureDetailModal = ({ measure, onClose }) => {
  if (!measure) return null;
  
  const statusStyle = STATUS_COLORS[measure.status] || STATUS_COLORS.off_track;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: measure.pillar_color }}
                />
                <span className="text-sm text-gray-500">{measure.pillar_name}</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">{measure.name}</h3>
              <p className="text-gray-600 mt-1">{measure.description}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <span className="sr-only">Close</span>×
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Score Overview */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <span className="text-sm text-gray-500">Current Value</span>
              <p className="text-2xl font-bold text-gray-900">{measure.current_value?.toFixed(1) || 0}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <span className="text-sm text-gray-500">Target</span>
              <p className="text-2xl font-bold text-blue-600">{measure.target}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <span className="text-sm text-gray-500">Gap to Target</span>
              <p className={`text-2xl font-bold ${measure.gap_to_target <= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {measure.gap_to_target > 0 ? '+' : ''}{measure.gap_to_target?.toFixed(1) || 0}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${statusStyle.bg} ${statusStyle.border} border`}>
              <span className={`text-sm ${statusStyle.text}`}>Status</span>
              <p className={`text-xl font-bold ${statusStyle.text} capitalize`}>
                {measure.status?.replace('_', ' ')}
              </p>
            </div>
          </div>
          
          {/* Trend Chart */}
          {measure.time_series && measure.time_series.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Trend Over Time</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={measure.time_series}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={measure.pillar_color} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={measure.pillar_color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      stroke="#9CA3AF"
                    />
                    <YAxis domain={[0, 100]} stroke="#9CA3AF" />
                    <Tooltip
                      formatter={(value) => [value?.toFixed(1), 'Value']}
                      labelFormatter={(label) => new Date(label).toLocaleDateString()}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={measure.pillar_color}
                      strokeWidth={2}
                      fill="url(#colorValue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          
          {/* Statistics */}
          {measure.statistics && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Statistics</h4>
              <div className="grid grid-cols-5 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">Min</span>
                  <p className="font-semibold">{measure.statistics.min?.toFixed(1)}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">Max</span>
                  <p className="font-semibold">{measure.statistics.max?.toFixed(1)}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">Average</span>
                  <p className="font-semibold">{measure.statistics.avg?.toFixed(1)}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">Total Change</span>
                  <p className={`font-semibold ${measure.statistics.total_change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {measure.statistics.total_change >= 0 ? '+' : ''}{measure.statistics.total_change?.toFixed(1)}
                  </p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">Data Points</span>
                  <p className="font-semibold">{measure.data_points}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Calculation Hint */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-1 flex items-center gap-2">
              <Info size={16} />
              Calculation Formula
            </h4>
            <p className="text-blue-800 font-mono text-sm">{measure.calculation_hint}</p>
          </div>
        </div>
        
        <div className="p-6 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Main LEI Dashboard Page
const LEIDashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Deep-link: /lei-dashboard?condition=<stage> opens the Conditions tab
  // and highlights the matching condition card (continuity from Home).
  const [highlightStage, setHighlightStage] = useState(searchParams.get('condition') || null);
  const { isSuperAdmin, viewAsOrg } = useAuth();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const [loading, setLoading] = useState(true);
  const [chainHealth, setChainHealth] = useState(null);
  const [chainLoading, setChainLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(
    new URLSearchParams(window.location.search).get('condition') ? 'conditions' : 'navigation'
  );
  const [overviewData, setOverviewData] = useState(null);
  const [measuresData, setMeasuresData] = useState([]);
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [pillarDetail, setPillarDetail] = useState(null);
  const [selectedMeasure, setSelectedMeasure] = useState(null);
  const [measureDetail, setMeasureDetail] = useState(null);
  const [insights, setInsights] = useState([]);
  
  // Data context filters
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(searchParams.get('organization_id') || viewAsOrg?.id || 'all');
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [selectedProgram, setSelectedProgram] = useState('all');
  
  // Fetch organizations (Super Admin only)
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchOrgs = async () => {
      const token = secureStorage.getItem('session_token');
      try {
        const res = await authedFetch(`${API_URL}/api/rbac/organizations`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setOrganizations(data.organizations || []);
        }
      } catch (e) { logger.error('Error fetching orgs:', e); }
    };
    fetchOrgs();
  }, [isSuperAdmin]);
  
  // Fetch cohorts and programs
  useEffect(() => {
    const fetchSegments = async () => {
      const token = secureStorage.getItem('session_token');
      if (!token) return;
      try {
        const [cohortsRes, programsRes] = await Promise.all([
          authedFetch(`${API_URL}/api/segments/cohorts`, { headers: { 'Authorization': `Bearer ${token}` } }),
          authedFetch(`${API_URL}/api/segments/programs`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        if (cohortsRes.ok) { const d = await cohortsRes.json(); setCohorts(d.cohorts || []); }
        if (programsRes.ok) { const d = await programsRes.json(); setPrograms(d.programs || []); }
      } catch (e) { logger.error('Error fetching segments:', e); }
    };
    fetchSegments();
  }, []);
  
  // Filter cohorts/programs by selected org
  const filteredCohorts = selectedOrg === 'all' ? cohorts : cohorts.filter(c => c.organization_id === selectedOrg);
  const filteredPrograms = selectedOrg === 'all' ? programs : programs.filter(p => p.organization_id === selectedOrg);
  
  // Reset cohort/program selection when org changes
  useEffect(() => {
    setSelectedCohort('all');
    setSelectedProgram('all');
  }, [selectedOrg]);
  
  // Build query string for filters
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedCohort !== 'all') params.append('cohort_id', selectedCohort);
    if (selectedProgram !== 'all') params.append('program_id', selectedProgram);
    if (selectedOrg && selectedOrg !== 'all') params.append('organization_id', selectedOrg);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [selectedCohort, selectedProgram, selectedOrg]);
  
  // Fetch overview data
  useEffect(() => {
    const fetchOverview = async () => {
      const token = secureStorage.getItem('session_token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      setLoading(true);
      const qs = buildFilterParams();
      try {
        const [overviewRes, measuresRes, insightsRes] = await Promise.all([
          authedFetch(`${API_URL}/api/lei-dashboard/overview${qs}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          authedFetch(`${API_URL}/api/lei-dashboard/comparison${qs}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          authedFetch(`${API_URL}/api/lei-dashboard/insights`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        if (overviewRes.ok) {
          const data = await overviewRes.json();
          setOverviewData(data);
        }
        
        if (measuresRes.ok) {
          const data = await measuresRes.json();
          setMeasuresData(data.measures || []);
        }
        
        if (insightsRes.ok) {
          const data = await insightsRes.json();
          setInsights(data.insights || []);
        }
      } catch (error) {
        logger.error('Error fetching LEI data:', error);
      }
      setLoading(false);
    };
    
    fetchOverview();
  }, [navigate, buildFilterParams]);

  // Fetch the value-transfer chain health (headline numbers + hand-off
  // leakage/amplification + cached AI narrative). Re-fetches on cohort/org
  // change so the chain is per-cohort.
  useEffect(() => {
    const fetchChain = async () => {
      const token = secureStorage.getItem('session_token');
      if (!token) return;
      setChainLoading(true);
      const params = new URLSearchParams();
      if (selectedCohort && selectedCohort !== 'all') params.append('cohort_id', selectedCohort);
      if (selectedOrg && selectedOrg !== 'all') params.append('organization_id', selectedOrg);
      const qs = params.toString() ? `?${params.toString()}` : '';
      try {
        const res = await authedFetch(`${API_URL}/api/board-brief/chain-health${qs}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) setChainHealth(await res.json());
      } catch (e) { logger.error('Error fetching chain health:', e); }
      setChainLoading(false);
    };
    fetchChain();
  }, [selectedCohort, selectedOrg]);
  
  // Fetch pillar detail
  const fetchPillarDetail = async (pillarId) => {
    const token = secureStorage.getItem('session_token');
    if (!token) return;
    
    try {
      const qs = buildFilterParams();
      const response = await authedFetch(`${API_URL}/api/lei-dashboard/by-pillar/${pillarId}${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPillarDetail(data);
        setSelectedPillar(pillarId);
        setActiveTab('conditions');
      }
    } catch (error) {
      logger.error('Error fetching pillar detail:', error);
    }
  };
  
  // Fetch measure detail
  const fetchMeasureDetail = async (measureId) => {
    const token = secureStorage.getItem('session_token');
    if (!token) return;
    
    try {
      const qs = buildFilterParams();
      const response = await authedFetch(`${API_URL}/api/lei-dashboard/by-measure/${measureId}${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const { measure, ...rest } = data;
        setMeasureDetail({ ...measure, ...rest });
        setSelectedMeasure(measureId);
      }
    } catch (error) {
      logger.error('Error fetching measure detail:', error);
    }
  };
  
  // Prepare radar chart data
  const radarData = overviewData?.pillar_scores?.map(p => ({
    pillar: p.short_name,
    score: p.score,
    fullMark: 100
  })) || [];

  // ── Missing Middle reframe: LEI pillars → navigation condition stages ──
  const conditions = buildConditions(overviewData);
  const navScore = overviewData?.overall_lei_score ?? null;
  const navBand = conditionBand(navScore);
  // Stage health for the AI Navigation Summary (reuses the gpt-4o engine).
  const condEntry = (key) => {
    const c = conditions.find((x) => x.key === key);
    return c && c.score != null
      ? { latest: c.score, changePct: null, signalCount: c.measures?.length || 1 }
      : null;
  };
  const stageHealthForAI = navScore != null ? {
    capability: condEntry('capability'),
    visibility: condEntry('visibility'),
    confidence: condEntry('confidence'),
    coordination: condEntry('coordination'),
    value: condEntry('value'),
    navigation: { latest: navScore, changePct: null, signalCount: 5 },
  } : null;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }
  
  const __pageTheme = getLightSectionTheme('Navy');
  const __heroActions = (
    <button
      onClick={() => navigate('/data-management')}
      className="flex items-center gap-2 px-4 py-2 bg-white text-teal-700 rounded-lg font-medium hover:bg-slate-50 text-sm"
    >
      <Database size={16} />
      Import Data
    </button>
  );
  // North-star tile — the value-transfer chain is the headline, NOT the LEI
  // score. We surface Chain Transfer Efficiency (value transfer) as the
  // north-star and ROI Health as its companion stat below. LEI is demoted to
  // a secondary "Navigation Conditions" input.
  const __money = (v) => {
    if (v == null) return '—';
    const a = Math.abs(v);
    const s = v < 0 ? '−' : '';
    return a >= 1e6 ? `${s}$${(a / 1e6).toFixed(1)}M` : `${s}$${Math.round(a / 1e3)}K`;
  };
  const __heroNorthStar = chainHealth ? {
    label: 'Learning Value Transfer Chain · overall health',
    value: chainHealth.chain_transfer_efficiency != null ? String(chainHealth.chain_transfer_efficiency) : '—',
    valueSuffix: '% efficiency',
    description: `Chain Transfer Efficiency — how well value carries from Learning through to Financial impact.`
      + (chainHealth.chain_transfer_band ? ` Currently ${chainHealth.chain_transfer_band}.` : '')
      + (chainHealth.net_leak_usd ? ` ~${__money(chainHealth.net_leak_usd)} is leaking across weak hand-offs; ${__money(chainHealth.net_amplify_usd)} is being amplified.` : ''),
    sparklineData: (chainHealth.stages || []).filter(s => s.score != null && !s.is_roi).map(s => ({ label: s.label, value: s.score })),
    sparklineFormatter: (v) => `${Math.round(v)}`,
    testId: 'lei-hero-northstar',
    whyPanel: {
      label: 'Overall Chain Health (two numbers)',
      value: `${chainHealth.chain_transfer_efficiency ?? '—'}% · ${chainHealth.roi_health?.multiple ?? '—'}x`,
      description: 'This page tracks the health of the learning value-transfer chain, not the LEI alone. Overall health = Chain Transfer Efficiency (how much modelled value survives each hand-off from Maturity → Learning → Capability → Behavior → Financial) shown alongside ROI Health (the blended financial return). LEI now sits below as one input (Navigation Conditions).',
      formula: 'Chain Transfer Efficiency = downstream value ÷ upstream value across hand-offs · ROI Health = blended $ return',
      breakdown: (chainHealth.handoffs || []).map(h => ({
        label: `${h.from} → ${h.to}`,
        value: `${h.pct > 0 ? '+' : ''}${h.pct}% ${h.word}`,
        hint: h.usd != null ? __money(h.usd) : undefined,
      })),
      links: [
        { label: 'See the full Board Brief', path: '/board-brief' },
        { label: 'Financial Health (ROI)', path: '/roi-health' },
        { label: 'Import KPI / measure data', path: '/data-management' },
      ],
    },
  } : (overviewData ? {
    label: 'Navigation Conditions · LEI = Navigation Conditions Index',
    value: (overviewData.overall_lei_score?.toFixed(0) || 0),
    valueSuffix: '/ 100',
    description: `Conditions are ${navBand.label} — the organisational conditions required for capability to convert into outcomes and value.`,
    testId: 'lei-hero-northstar',
  } : null);
  // Page-scoped narrative — pillar with biggest QoQ gain vs
  // pillar with biggest QoQ decline. Falls back to a
  // steady-state read-out if the backend hasn't surfaced QoQ
  // pillar data yet so the band still anchors the page.
  const __heroNarrative = overviewData ? (() => {
    const pillars = (overviewData.pillar_scores || []).filter(p => typeof p.qoq_delta === 'number' && p.qoq_delta !== 0);
    if (!pillars.length) {
      const measureCount = overviewData.measures_with_data || 0;
      const totalMeasures = overviewData.total_measures || 15;
      return {
        headline: `${measureCount} of ${totalMeasures} measures contributing · holding pattern this quarter`,
      };
    }
    const sortedDesc = [...pillars].sort((a, b) => b.qoq_delta - a.qoq_delta);
    const top = sortedDesc[0];
    const bottom = sortedDesc[sortedDesc.length - 1];
    const gainer = top && top.qoq_delta > 0
      ? { label: top.short_name || top.name, deltaText: `+${top.qoq_delta} pts` }
      : null;
    const decliner = bottom && bottom.qoq_delta < 0
      ? { label: bottom.short_name || bottom.name, deltaText: `${bottom.qoq_delta} pts` }
      : null;
    if (!gainer && !decliner) {
      return { headline: `${pillars.length} pillar${pillars.length !== 1 ? 's' : ''} stable · all metrics holding pattern` };
    }
    return { gainer, decliner };
  })() : null;
  // Drop Overall LEI from stats grid — it's the north-star now.
  const __heroStats = [
    {
      label: 'ROI Health',
      value: chainHealth?.roi_health?.multiple != null ? `${chainHealth.roi_health.multiple}x` : '—',
      testId: 'lei-stat-roi-health',
      whyPanel: {
        label: 'ROI Health',
        value: chainHealth?.roi_health?.multiple != null ? `${chainHealth.roi_health.multiple}x` : '—',
        description: 'The blended financial return across all business cases — the second half of this page overall health (shown alongside Chain Transfer Efficiency, not blended into a single number). Bands: Strong (>=3x), Healthy (>=1.5x), Watch (>=1x), At Risk (<1x).'
          + (chainHealth?.net_benefit != null ? ` Net benefit ~ ${__money(chainHealth.net_benefit)} on the modelled value pool.` : ''),
        breakdown: [
          { label: 'Band', value: chainHealth?.roi_health?.band || '—' },
          { label: 'Value pool', value: __money(chainHealth?.value_pool) },
        ],
        links: [{ label: 'Open Financial Health (ROI)', path: '/roi-health' }],
      },
    },
    {
      label: 'Navigation Conditions (LEI)',
      value: navBand.label,
      testId: 'lei-stat-status',
      whyPanel: {
        label: 'Navigation Conditions Band (LEI — an input)',
        value: navBand.label,
        description: 'The LEI composite, relabelled as Navigation Conditions. It is ONE INPUT into chain health — the organisational conditions required for capability to convert into value — not this page overall health. Bands: Constrained (< 50), Emerging (50-59), Stable (60-69), Healthy (70-79), Strong (>= 80).',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Bands change less often than the raw score, so they are useful in board narratives ("we moved from At Risk to Stable this quarter").' },
          { label: 'What to do', value: '', hint: 'If status is Critical or At Risk, jump straight to the lowest-scoring pillar in the Navigation Conditions section below.' },
        ],
        links: [{ label: 'Pin to Quarterly Deck', path: '/' }],
      },
    },
    {
      label: 'Measures with Data',
      value: `${overviewData?.measures_with_data || 0} / ${overviewData?.total_measures || 15}`,
      testId: 'lei-stat-measures',
      whyPanel: {
        label: 'Measures with Data',
        value: `${overviewData?.measures_with_data || 0} / ${overviewData?.total_measures || 15}`,
        description: 'How many of the 15 LEI measures have any imported data this quarter. The rest sit at neutral and pull the composite toward the middle.',
        formula: '(measures with imports this quarter) ÷ (total LEI measures defined)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Below 10/15 means the LEI is being computed from a partial picture — interpret the score with caution.' },
          { label: 'What to do', value: '', hint: (overviewData?.measures_with_data || 0) < 10
              ? 'Open Data Management → Component Data Import to backfill the missing measures. Even a single quarter\'s worth of data per measure dramatically tightens the composite.'
              : 'Strong coverage. Drill into any pillar in the radar below to see component-level breakdowns.' },
        ],
        links: [
          { label: 'Open Data Management', path: '/data-management' },
          { label: 'Component Data Import', path: '/component-import' },
        ],
      },
    },
    {
      label: 'Conditions Tracked',
      value: `${radarData.length} / 5`,
      testId: 'lei-stat-pillars',
      whyPanel: {
        label: 'Pillars Tracked',
        value: `${radarData.length} / 5`,
        description: 'Of the 5 LEI pillars (Capability, Application, Effectiveness, Efficiency, Engagement), how many have at least one measure contributing data this quarter.',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'A 5/5 pillar coverage gives you a balanced view. 3/5 means 2 pillars are dark — your LEI is structurally biased toward what you can measure.' },
          { label: 'What to do', value: '', hint: radarData.length === 5
              ? 'Full coverage. Use the radar below to find the lowest pillar and target your next intervention there.'
              : 'Add at least one measure per missing pillar — even a single proxy KPI is enough to flip a dark pillar visible.' },
        ],
        links: [
          { label: 'LEI Reference (measures by pillar)', path: '/lei-reference' },
          { label: 'Component Data Import', path: '/component-import' },
        ],
      },
    },
  ];
  return (
    <PageShell
      theme={__pageTheme}
      stickyTitle="Navigation Health"
      hero={(
      <PageHero
        title="Navigation Health"
        description="Health of the learning value-transfer chain — how much value amplifies or leaks across Learning, Capability, Transfer and Financial, and how organizational maturity erodes value at every stage. Headlined by Chain Transfer Efficiency and ROI Health."
        theme={__pageTheme}
        icon={LineChartIcon}
        actions={__heroActions}
        testId="lei-dashboard-hero"
      />
    )}>
    <div className="space-y-6">

        {/* ── Value Transfer Chain Health — the page centerpiece ── */}
        <NavigationChainHealth
          data={chainHealth}
          loading={chainLoading}
          cohorts={filteredCohorts}
          selectedCohort={selectedCohort}
          onCohortChange={setSelectedCohort}
          isDark={isDark}
          navigate={navigate}
        />

        {/* Proactive AI insights across the caller's pinned reports
            (LEI surface). Lazy-loaded, cached server-side for 24h.
            Hides itself when nothing material to flag. */}
        <ProactiveInsightsCard surface="lei_dashboard" />

        {/* Pinned AI Report Builder tiles (LEI surface). Renders
            nothing when the current user has no pins on this surface. */}
        <PinnedReportsSection surface="lei_dashboard" title="Pinned reports" />

        {/* Data Context Filter Bar */}
        {(cohorts.length > 0 || programs.length > 0) && (
          <div className="bg-white rounded-xl border p-4 mb-6 flex flex-wrap items-center gap-4" data-testid="lei-data-filter-bar">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
              <Filter size={16} />
              Data Context
            </div>
            
            {isSuperAdmin && organizations.length > 0 && (
              <div className="flex items-center gap-2">
                <Building2 size={14} className="text-gray-400" />
                <select
                  value={selectedOrg}
                  onChange={(e) => setSelectedOrg(e.target.value)}
                  className="text-sm text-slate-700 border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  data-testid="lei-org-filter"
                >
                  <option value="all">All Organizations</option>
                  {organizations.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            )}
            
            {filteredCohorts.length > 0 && (
              <div className="flex items-center gap-2">
                <Users size={14} className="text-gray-400" />
                <select
                  value={selectedCohort}
                  onChange={(e) => setSelectedCohort(e.target.value)}
                  className="text-sm text-slate-700 border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  data-testid="lei-cohort-filter"
                >
                  <option value="all">All Cohorts</option>
                  {filteredCohorts.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.organization_name && c.organization_id !== 'demo' ? ` · ${c.organization_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {filteredPrograms.length > 0 && (
              <div className="flex items-center gap-2">
                <GraduationCap size={14} className="text-gray-400" />
                <select
                  value={selectedProgram}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                  className="text-sm text-slate-700 border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  data-testid="lei-program-filter"
                >
                  <option value="all">All Programs</option>
                  {filteredPrograms.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.organization_name && p.organization_id !== 'demo' ? ` · ${p.organization_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {(selectedOrg !== 'all' || selectedCohort !== 'all' || selectedProgram !== 'all') && (
              <button
                onClick={() => { setSelectedOrg('all'); setSelectedCohort('all'); setSelectedProgram('all'); }}
                className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1"
                data-testid="lei-clear-filters"
              >
                <RefreshCw size={12} />
                Clear Filters
              </button>
            )}
          </div>
        )}
        
        {/* Tab Navigation — Navigation (default) / Conditions / Measures */}
        <div className="pt-2 border-t border-slate-200">
          <h2 className="text-base font-bold text-slate-800 mt-4">Navigation Conditions <span className="text-xs font-medium text-slate-400">(LEI — an input into chain health)</span></h2>
          <p className="text-sm text-slate-500 mb-3">The organisational conditions (LEI pillars &amp; measures) that determine how well capability converts into value further down the chain.</p>
        </div>
        <div className="flex gap-2 mb-6">
          {[
            { key: 'navigation', label: 'Navigation' },
            { key: 'conditions', label: 'Conditions' },
            { key: 'measure', label: 'Measures' },
          ].map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => { if (tab.key !== 'conditions') { setPillarDetail(null); setSelectedPillar(null); } setHighlightStage(null); setActiveTab(tab.key); }}
                data-testid={`lei-tab-${tab.key}`}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  active ? 'bg-slate-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Navigation View (default) ──────────────────────────── */}
        {activeTab === 'navigation' && (
          <div className="space-y-6" data-testid="lei-navigation-view">
            <NavigationConditionsRibbon conditions={conditions} />
            <ConditionPropagation conditions={conditions} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ConversionEfficiencyCard conditions={conditions} />
              <NavigationConstraintsPanel conditions={conditions} />
            </div>
            {stageHealthForAI && (
              <div className="space-y-2" data-testid="navigation-intelligence">
                <h2 className="text-base font-bold text-slate-800">Navigation Intelligence</h2>
                <NavigationSummaryCard orgId={selectedOrg} stageHealth={stageHealthForAI} />
              </div>
            )}
          </div>
        )}

        {/* Overview Tab (legacy — retained behind nothing; kept for safety) */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Score and Radar */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Overall Score */}
              <div className="bg-white rounded-xl border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall LEI Score</h2>
                <LEIScoreGauge
                  score={overviewData?.overall_lei_score || 0}
                  status={overviewData?.overall_status}
                />
                <div className="mt-4 text-center">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    overviewData?.overall_status === 'excellent' ? 'bg-emerald-100 text-emerald-700' :
                    overviewData?.overall_status === 'good' ? 'bg-blue-100 text-blue-700' :
                    overviewData?.overall_status === 'needs_attention' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-rose-100 text-rose-700'
                  }`}>
                    {overviewData?.overall_status?.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                
                {/* Trend mini chart */}
                {overviewData?.trend && overviewData.trend.length > 1 && (
                  <div className="mt-6 h-24">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={overviewData.trend}>
                        <Area
                          type="monotone"
                          dataKey="score"
                          stroke="#3B82F6"
                          fill="#3B82F6"
                          fillOpacity={0.1}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
              
              {/* Radar Chart */}
              <div className="bg-white rounded-xl border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Pillar Performance</h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#E5E7EB" />
                      <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                      <Radar
                        name="Score"
                        dataKey="score"
                        stroke="#3B82F6"
                        fill="#3B82F6"
                        fillOpacity={0.3}
                      />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            
            {/* Pillar Cards */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Pillar Breakdown</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {overviewData?.pillar_scores?.map((pillar) => (
                  <PillarCard
                    key={pillar.id}
                    pillar={pillar}
                    onClick={() => fetchPillarDetail(pillar.id)}
                  />
                ))}
              </div>
            </div>
            
            {/* Insights */}
            {insights.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Insights</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {insights.map((insight, idx) => (
                    <div
                      key={insight.title || `insight-${idx}`}
                      className={`p-4 rounded-lg border ${
                        insight.type === 'success' ? 'bg-emerald-50 border-emerald-200' :
                        insight.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                        'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {insight.type === 'success' && <CheckCircle2 className="text-emerald-600 flex-shrink-0" size={20} />}
                        {insight.type === 'warning' && <AlertTriangle className="text-yellow-600 flex-shrink-0" size={20} />}
                        {insight.type === 'info' && <Info className="text-blue-600 flex-shrink-0" size={20} />}
                        <div>
                          <h4 className={`font-semibold ${
                            insight.type === 'success' ? 'text-emerald-900' :
                            insight.type === 'warning' ? 'text-yellow-900' : 'text-blue-900'
                          }`}>
                            {insight.title}
                          </h4>
                          <p className={`text-sm mt-1 ${
                            insight.type === 'success' ? 'text-emerald-700' :
                            insight.type === 'warning' ? 'text-yellow-700' : 'text-blue-700'
                          }`}>
                            {insight.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Conditions Tab (selection) — repositioned LEI pillars as stages */}
        {activeTab === 'conditions' && !pillarDetail && (
          <div className="space-y-6" data-testid="lei-conditions-view">
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Navigation Conditions</h2>
              <p className="text-gray-600 mb-6 text-sm">Your five LEI pillars, repositioned as Missing Middle condition stages. Same scores — tap any to drill into its measures.</p>
              <ConditionsGrid conditions={conditions} highlightStage={highlightStage} onSelect={(pillarId) => fetchPillarDetail(pillarId)} />
            </div>
          </div>
        )}

        {/* Pillar Detail Tab (when pillar selected) */}
        {activeTab === 'conditions' && pillarDetail && (
          <div className="space-y-6">
            {/* Back button */}
            <button
              onClick={() => {
                setPillarDetail(null);
                setSelectedPillar(null);
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              ← Back to Conditions
            </button>
            
            {/* Pillar Header */}
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className="w-4 h-16 rounded-full"
                    style={{ backgroundColor: pillarDetail.pillar.color }}
                  />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{pillarDetail.pillar.name}</h2>
                    <p className="text-gray-600">{pillarDetail.pillar.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-4xl font-bold" style={{ color: pillarDetail.pillar.color }}>
                    {pillarDetail.pillar.score?.toFixed(0)}
                  </span>
                  <span className="text-gray-500 text-xl">/100</span>
                  <p className="text-sm text-gray-500">{(pillarDetail.pillar.weight * 100).toFixed(0)}% weight</p>
                </div>
              </div>
              
              {/* Summary */}
              <div className="flex items-center gap-6 mt-6 pt-6 border-t">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-emerald-600" size={20} />
                  <span className="font-semibold">{pillarDetail.summary.on_track}</span>
                  <span className="text-gray-500">On Track</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertCircle className="text-yellow-600" size={20} />
                  <span className="font-semibold">{pillarDetail.summary.at_risk}</span>
                  <span className="text-gray-500">At Risk</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="text-rose-600" size={20} />
                  <span className="font-semibold">{pillarDetail.summary.off_track}</span>
                  <span className="text-gray-500">Off Track</span>
                </div>
              </div>
            </div>
            
            {/* Measures */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Measures</h3>
              <div className="space-y-3">
                {pillarDetail.measures?.map((measure) => (
                  <div
                    key={measure.id}
                    onClick={() => fetchMeasureDetail(measure.id)}
                    className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{measure.name}</span>
                      <span className={`text-xs px-2 py-1 rounded ${STATUS_COLORS[measure.status]?.bg} ${STATUS_COLORS[measure.status]?.text}`}>
                        {measure.status?.replace('_', ' ')}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.min(100, measure.progress || 0)}%`,
                              backgroundColor: pillarDetail.pillar.color
                            }}
                          />
                        </div>
                      </div>
                      <span className="font-semibold text-gray-900 w-20 text-right">
                        {measure.value?.toFixed(0)}/{measure.target}
                      </span>
                      <div className={`flex items-center gap-1 w-24 ${
                        measure.change >= 0 ? 'text-emerald-600' : 'text-rose-600'
                      }`}>
                        {measure.change >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                        <span className="text-sm">{Math.abs(measure.change_percent || 0).toFixed(1)}%</span>
                      </div>
                    </div>
                    
                    {/* Mini sparkline */}
                    {measure.time_series && measure.time_series.length > 1 && (
                      <div className="mt-2 h-8">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={measure.time_series}>
                            <Area
                              type="monotone"
                              dataKey="value"
                              stroke={pillarDetail.pillar.color}
                              fill={pillarDetail.pillar.color}
                              fillOpacity={0.1}
                              strokeWidth={1}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* By Measure Tab */}
        {activeTab === 'measure' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">All Measures</h2>
              <div className="space-y-2">
                {measuresData.map((measure) => (
                  <MeasureRow
                    key={measure.id}
                    measure={measure}
                    onClick={() => fetchMeasureDetail(measure.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Measure Detail Modal */}
      {measureDetail && (
        <MeasureDetailModal
          measure={measureDetail}
          onClose={() => {
            setMeasureDetail(null);
            setSelectedMeasure(null);
          }}
        />
      )}
    </PageShell>
  );
};

export default LEIDashboardPage;
